import { 
  Target, 
  Eye, 
  Shield, 
  Heart, 
  Lightbulb, 
  Leaf, 
  Users, 
  Award,
  CheckCircle,
  Calendar
} from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';

const About = () => {
  const values = [
    {
      icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Safety",
      description: "We prioritize safety in every aspect of our operations, ensuring zero compromise on health and environmental protection."
    },
    {
      icon: <Heart className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Integrity",
      description: "We operate with complete transparency and honesty, building trust through consistent ethical practices."
    },
    {
      icon: <Lightbulb className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Innovation",
      description: "We continuously invest in cutting-edge technology and methods to improve our waste management solutions."
    },
    {
      icon: <Leaf className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Sustainability",
      description: "Environmental responsibility drives our mission to create a circular economy through waste transformation."
    },
    {
      icon: <Users className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Customer Focus",
      description: "We tailor our services to meet specific client needs, ensuring satisfaction and long-term partnerships."
    }
  ];

  const certifications = [
    { name: "PPDA", description: "Public Procurement and Disposal of Public Assets Authority" },
    { name: "NEMA", description: "National Environment Management Authority" },
    { name: "KCCA", description: "Kampala Capital City Authority" },
    { name: "NDA", description: "National Drug Authority" }
  ];

  const milestones = [
    { year: "2021", title: "Company Founded", description: "BioHaz Waste Solutions established in Kampala" },
    { year: "2022", title: "Major Partnerships", description: "Partnerships with Ministry of Health and Mulago Hospital" },
    { year: "2023", title: "Service Expansion", description: "Extended services to include e-waste and textile treatment" },
    { year: "2024", title: "Innovation Focus", description: "Launched advanced recycling and circular economy initiatives" }
  ];

  return (
    <div className="w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-20 xl:py-24 bg-gradient-to-br from-[#295cab] to-[#1e4a8a] text-white w-full">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12 text-center w-full">
          <AnimatedSection animation="fade-in">
            <h1 className="text-2xl sm:text-4xl md:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6">About BioHaz Waste Solutions</h1>
            <p className="text-base sm:text-lg md:text-xl xl:text-2xl max-w-3xl mx-auto leading-relaxed px-2 sm:px-4">
              Transforming hazardous waste into valuable resources while building safer, cleaner societies across Uganda
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10 md:gap-12">
            <AnimatedSection animation="slide-in-left">
              <div className="h-full bg-gradient-to-br from-blue-50 to-blue-100 p-6 sm:p-8 rounded-2xl">
                <div className="flex items-center space-x-3 mb-4 sm:mb-6">
                  <Target className="w-6 h-6 sm:w-8 sm:h-8 text-[#295cab]" />
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Our Mission</h2>
                </div>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  To provide comprehensive, safe, and sustainable waste management solutions that protect public health, 
                  preserve the environment, and transform waste into valuable resources. We are committed to being 
                  Uganda's most trusted partner in biohazard and hazardous waste management.
                </p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection animation="slide-in-right">
              <div className="h-full bg-gradient-to-br from-blue-50 to-blue-100 p-6 sm:p-8 rounded-2xl">
                <div className="flex items-center space-x-3 mb-4 sm:mb-6">
                  <Eye className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Our Vision</h2>
                </div>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  To be the leading waste management company in East Africa, recognized for our innovation, 
                  sustainability practices, and contribution to a circular economy. We envision a future where 
                  every piece of waste becomes a resource for building healthier communities.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Company History */}
      <section className="relative py-10 sm:py-14 md:py-16 xl:py-20 overflow-hidden bg-gray-100 w-full">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/garbage.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            opacity: 0.4,
            filter: 'grayscale(80%) brightness(1.1)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/30"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12">
          <AnimatedSection animation="fade-in" className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Since our establishment in 2021, we've been at the forefront of waste management innovation in Uganda
            </p>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {milestones.map((milestone, index) => (
              <AnimatedSection 
                key={index} 
                animation="fade-in" 
                delay={index * 100}
                className="h-full"
              >
                <div className="h-full bg-white/95 backdrop-blur-sm p-4 sm:p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className="flex items-center space-x-2 mb-3 sm:mb-4">
                    <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-[#295cab]" />
                    <span className="text-xl sm:text-2xl font-bold text-[#295cab]">{milestone.year}</span>
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                  <p className="text-gray-600 text-sm sm:text-base">{milestone.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-in" className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              These values guide every decision we make and every service we provide
            </p>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-8 md:gap-10">
            {values.map((value, index) => (
              <AnimatedSection 
                key={index} 
                animation="scale-in" 
                delay={index * 100}
                className="h-full"
              >
                <div className="h-full text-center p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl hover:shadow-lg transition-all duration-300">
                  <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-blue-50 text-[#295cab] rounded-full mb-4 sm:mb-6 mx-auto">
                    {value.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{value.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-10 sm:py-14 md:py-16 xl:py-20 bg-gradient-to-br from-[#295cab] to-[#1e4a8a] text-white w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-in" className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold mb-4">Compliance & Accreditation</h2>
            <p className="text-base sm:text-lg md:text-xl xl:text-2xl max-w-2xl mx-auto px-2 sm:px-4">
              We maintain the highest standards of compliance with all relevant regulatory bodies
            </p>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {certifications.map((cert, index) => (
              <AnimatedSection 
                key={index} 
                animation="scale-in" 
                delay={index * 100}
                className="h-full"
              >
                <div className="h-full bg-white/10 backdrop-blur-sm p-4 sm:p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300">
                  <div className="flex items-center space-x-3 mb-3 sm:mb-4">
                    <Award className="w-6 h-6 sm:w-8 sm:h-8 text-blue-300" />
                    <h3 className="text-lg sm:text-xl font-bold">{cert.name}</h3>
                  </div>
                  <p className="text-blue-100 text-sm sm:text-base">{cert.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <AnimatedSection animation="slide-in-left" className="order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">Why Choose BioHaz Waste Solutions?</h2>
              <div className="space-y-3 sm:space-y-4">
                {[
                  "24/7 emergency response capability",
                  "Fully certified and compliant operations",
                  "Advanced treatment and disposal technologies",
                  "Comprehensive waste tracking and documentation",
                  "Experienced and trained personnel",
                  "Commitment to environmental sustainability",
                  "Competitive pricing and flexible service packages",
                  "Strong partnerships with healthcare institutions"
                ].map((benefit, index) => (
                  <AnimatedSection 
                    key={index} 
                    animation="fade-in" 
                    delay={index * 50}
                  >
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#295cab] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm sm:text-base">{benefit}</span>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </AnimatedSection>
            
            <AnimatedSection animation="slide-in-right" className="relative order-1 lg:order-2">
              <div className="relative">
                <img 
                  src="/teamgroup.jpg" 
                  alt="Professional Team" 
                  className="rounded-xl shadow-2xl w-full"
                />
                <AnimatedSection 
                  animation="scale-in" 
                  delay={400}
                  className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6"
                >
                  <div className="bg-[#295cab] text-white p-4 sm:p-6 rounded-lg shadow-lg">
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl font-bold">500+</div>
                      <div className="text-xs sm:text-sm">Satisfied Clients</div>
                    </div>
                  </div>
                </AnimatedSection>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;